import { useState } from "react";
import { ingestApi } from "../../../api";

export type UploadItem = {
  file: File;
  status: "queued" | "uploading" | "done" | "error";
  error?: string;
};

// Valid file types for upload
export const VALID_FILE_TYPES = [
  // PDF formats
  "application/pdf",

  // PowerPoint formats
  "application/vnd.openxmlformats-officedocument.presentationml.presentation", // pptx
  "application/vnd.ms-powerpoint", // ppt
  "application/vnd.openxmlformats-officedocument.presentationml.template", // potx
  "application/vnd.ms-powerpoint.template.macroEnabled.12", // potm
  "application/vnd.ms-powerpoint.slideshow.macroEnabled.12", // ppsm
  "application/vnd.openxmlformats-officedocument.presentationml.slideshow", // ppsx
  "application/vnd.ms-powerpoint.presentation.macroEnabled.12", // pptm

  // Word formats
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // docx
  "application/msword", // doc
  "application/vnd.ms-word.document.macroEnabled.12", // docm
  "application/vnd.openxmlformats-officedocument.wordprocessingml.template", // dotx
  "application/vnd.ms-word.template.macroEnabled.12", // dotm

  // JSON format
  "application/json",
];

// Maximum file size (50MB in bytes)
// Explicit calculation: 50 * 1024 * 1024 = 52,428,800 bytes
export const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB in bytes

// Maximum number of files in queue
export const MAX_QUEUE_SIZE = 10;

// A helper function to validate file extensions as fallback
export function hasValidExtension(filename: string): boolean {
  const validExtensions = [
    ".pdf",
    ".ppt",
    ".pptx",
    ".pptm",
    ".pot",
    ".potx",
    ".potm",
    ".pps",
    ".ppsx",
    ".ppsm",
    ".doc",
    ".docx",
    ".docm",
    ".dot",
    ".dotx",
    ".dotm",
    ".json",
  ];

  const ext = filename.toLowerCase().split(".").pop();
  return ext ? validExtensions.includes(`.${ext}`) : false;
}

type UseUploadQueueProps = {
  orgId: string;
  orgCategory: "public" | "internal" | "personal";
  onComplete?: () => void;
};

export function useUploadQueue({
  orgId,
  orgCategory,
  onComplete,
}: UseUploadQueueProps) {
  const [queue, setQueue] = useState<UploadItem[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  // Add new files to the queue with initial "queued" status.
  const addFiles = (files: File[]) => {
    // Check if adding more files would exceed the queue limit
    const remainingSlots = MAX_QUEUE_SIZE - queue.length;
    const exceedsLimit = remainingSlots <= 0;

    // If queue is already full, return without adding files
    if (exceedsLimit) {
      return {
        total: files.length,
        accepted: 0,
        rejected: files.length,
        queueLimitExceeded: true,
      };
    }

    // Filter valid files only
    const validFiles = files.filter((file) => {
      // Check file type using MIME type (primary method)
      const isValidType = VALID_FILE_TYPES.includes(file.type);

      // If MIME type check fails, fall back to extension check
      const isValidExtension = !isValidType && hasValidExtension(file.name);

      // Check file size (fix: ensure size is compared in bytes)
      // 1 MB = 1024 KB, 1 KB = 1024 bytes
      const isValidSize = file.size <= MAX_FILE_SIZE;

      return (isValidType || isValidExtension) && isValidSize;
    });

    // Limit the number of files to the remaining slots
    const filesToAdd = validFiles.slice(0, remainingSlots);
    const queueLimitReached = validFiles.length > remainingSlots;

    // Add only valid files to queue (up to the limit)
    const newItems = filesToAdd.map((file) => ({
      file,
      status: "queued" as const,
    }));

    setQueue((prev) => [...prev, ...newItems]);

    // Return info about rejected and limited files for UI notification
    return {
      total: files.length,
      accepted: filesToAdd.length,
      rejected: files.length - filesToAdd.length,
      queueLimitReached: queueLimitReached,
    };
  };

  // Helper to update a specific queue item.
  const updateQueueItem = (index: number, item: Partial<UploadItem>) => {
    setQueue((prev) => {
      const newQueue = [...prev];
      newQueue[index] = { ...newQueue[index], ...item };
      return newQueue;
    });
  };

  // Helper to remove an item from the queue
  const removeQueueItem = (index: number) => {
    setQueue((prev) => {
      const newQueue = [...prev];
      newQueue.splice(index, 1);
      return newQueue;
    });
  };

  // Helpers extracted from the component
  async function getUploadUrl(file: File) {
    return await ingestApi.generateUploadUrl({
      org_id: orgId,
      org_category: orgCategory,
      doc_name: file.name,
      doc_id: file.name,
      doc_source: "upload",
      doc_tags: [],
    });
  }

  // Process the upload queue sequentially.
  const processQueue = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    for (let i = 0; i < queue.length; i++) {
      const uploadItem = queue[i];
      // Skip items that are already done.
      if (uploadItem.status === "done") continue;
      // Skip items with errors
      if (uploadItem.status === "error") continue;
      try {
        updateQueueItem(i, { status: "uploading" });
        const { upload_url, doc_id } = await getUploadUrl(uploadItem.file);
        await uploadFile(uploadItem.file, upload_url);

        // Trigger async processing but don't wait for completion
        ingestApi
          .processUploadedDocument(orgCategory, orgId, doc_id)
          .catch((error: unknown) =>
            console.error(`Error triggering processing for ${doc_id}:`, error)
          );

        // Mark as done immediately after upload and triggering processing
        updateQueueItem(i, { status: "done" });
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error);
        updateQueueItem(i, { status: "error", error: message });
      }
    }
    setIsProcessing(false);
    if (onComplete) {
      onComplete();
    }
  };

  return {
    queue,
    addFiles,
    processQueue,
    removeQueueItem,
    isProcessing,
    setQueue,
  };
}

export async function uploadFile(file: File, uploadUrl: string) {
  // In development, proxy through the Vite dev server to avoid CORS
  // preflight failures when PUTting directly to Azure Blob Storage.
  const url = import.meta.env.DEV ? "/__blob_proxy" : uploadUrl;
  const headers: Record<string, string> = {
    "x-ms-blob-type": "BlockBlob",
    "Content-Type": file.type || "application/octet-stream",
  };
  if (import.meta.env.DEV) {
    headers["x-target-url"] = uploadUrl;
  }

  const response = await fetch(url, {
    method: "PUT",
    headers,
    body: file,
  });
  if (!response.ok) {
    throw new Error(`Upload failed for ${file.name}: ${response.statusText}`);
  }
}
